/**
 * The logic behind Builder's sandbox panel (LO-187), without React or a
 * network, so it can be read and tested on its own.
 *
 * ## What this keeps, and what it deliberately does not
 *
 * The panel draws what the kernel already says, from the endpoints the Builder
 * MCP tools read (LO-184): the request (`GET /manifest-requests/{id}`), the QA
 * report and gate (`GET /sandboxes/{id}/qa`), the call log
 * (`GET /sandboxes/{id}/calls`) and the egress log
 * (`GET /sandboxes/{id}/egress`). None of those is reshaped here into a report
 * of Builder's own: a check's title, detail and reproduction steps are shown as
 * the kernel stored them, and the sentence that says a pass is a pass
 * (`label`) is the kernel's, not one written here.
 *
 * What this does decide is **presentation**: which of a report's checks come
 * first, what a state is called, and what the Submit button says when it is not
 * yet pressable. The one *rule* it must not copy is whether a run passes. That
 * is `lopie_kernel.sandbox_qa.verdict` and the gate is `gate.passed_for_current`
 * on the same response; the button reads that field and does not re-derive it
 * from the run, because a second copy of "does this pass" is correct the day it
 * is written and wrong the first time the real one changes. The hard gate
 * (LO-183) is the kernel refusing `submit`; whatever it says is shown verbatim,
 * and nothing here treats an enabled button as permission.
 */

import { wordsOf } from "./impact.js";

/** What a pass is called, wherever it appears. The kernel sends this string on
 *  a passing run (`label`); it is repeated here only so a response that lacks it
 *  can never be drawn as a bare "passed". Never a safety claim. */
export const PASS_LABEL = "passed against synthetic data and these personas";

// -- the sandbox's own state ----------------------------------------------------

const STALE_WHY = {
  build: "The request now names a different build from the one this sandbox was made from.",
  ceiling: "The request's ceiling has changed since this sandbox was pointed at it, so it is testing a ceiling nobody is asking for now.",
  closed: "The request is no longer open, and a sandbox was meant to be removed with it.",
};

/**
 * What the provisioner recorded when it noticed a sandbox had not come up in
 * time (LO-192), or `null`. One record, written twice by one writer
 * (`lopie_kernel.sandbox_health.record`): the control plane's copy rides on the
 * current sandbox (`GET /builder/sandboxes/{id}`), and the kernel's is on the
 * request's own view (`sandbox.health`), which is what `sandbox_status` reads.
 * The control plane's is preferred because it is the first to be written, and
 * either is enough: the kernel's is the only one that survives the control plane
 * not answering.
 *
 * Drawn as it is. Whether a sandbox has stalled is the provisioner's decision
 * and the sentence is the kernel's; nothing here decides or rewords either.
 */
export function recoveryOf(request, control) {
  const found = control?.current?.recovery || control?.recovery || request?.sandbox?.health || null;
  return found && typeof found === "object" && found.state ? found : null;
}

/** A record's state in a title and a severity. The severity is for the colour of
 *  the box; the title is what says it, because colour alone does not. */
export function recoveryWords(recovery) {
  return {
    stalled: { title: "Stalled, and being replaced", severity: "warning" },
    replaced: { title: "Stalled once, replaced, and the replacement is coming up", severity: "warning" },
    failed: { title: "Stalled twice, not tried again", severity: "error" },
    recovered: { title: "Came up after a stall", severity: "info" },
  }[recovery?.state] || null;
}

/** Seconds as whole minutes, at least one, or nothing for a value that is not a number. */
export function minutesWord(seconds) {
  if (typeof seconds !== "number" && typeof seconds !== "string") return "";
  const n = Number(seconds);
  if (!Number.isFinite(n)) return "";
  const m = Math.max(Math.floor(n / 60), 1);
  return `${m} ${m === 1 ? "minute" : "minutes"}`;
}

/**
 * What to say about one request's sandbox, or `null` when there is nothing yet.
 *
 * The same rule the console's Requests screen uses (`apps/console/src/sections/
 * sandbox.js`, LO-190), from the same two sources, because two systems know
 * different halves and neither knows the other's: the kernel's request view
 * carries `sandbox` (is a client pointed at this request, and is that pointer
 * still for the request's build and ceiling) and the control plane's
 * `current` says whether anything is up and seeded. `control` is that body,
 * `undefined` while it has not been asked and `null` when it could not be read
 * or there is none.
 *
 *  - **stale** wins over everything: a sandbox for another build or ceiling is
 *    running something that is not what would be approved, whatever its health.
 *  - **provisioning**, **seeding**, **ready** are the control plane's, in order.
 *  - **failed** is neither of those and must not read as in progress.
 *  - **unknown** is the kernel knowing a client is pointed and the control plane
 *    not answering: a pointer does not say a function is running, so "running"
 *    is not claimed.
 *
 * A sandbox that did not come up in time (LO-192) is said as what the provisioner
 * recorded, in its words (`recoveryOf`): **provisioning** while it was replaced
 * and the replacement is coming up, **failed** once the replacement stalled too,
 * and in neither case the composition's own `message`, which is a count and the
 * first resource it found refused, and was once a transient one from another
 * resource. A sandbox that came up after all is whatever it now is, with the
 * record on it, because it was late.
 */
export function sandboxState(request, control) {
  const pointer = request?.sandbox || null;
  const wanted = request?.proposed?.build_id || "";
  const live = control?.current || null;
  const rec = recoveryOf(request, control);
  const unresolved = rec && ["stalled", "replaced", "failed"].includes(rec.state);
  // Pointed means it came up, whatever the record has not yet been told.
  const stalled = unresolved && !(pointer && pointer.pointed);

  if (live && wanted && live.buildId && live.buildId !== wanted) {
    return built("stale", live, STALE_WHY.build, "", "build", rec);
  }
  if (pointer && pointer.pointed && !pointer.fresh) {
    return built("stale", live,
      STALE_WHY[pointer.stale] || "The kernel no longer treats this sandbox as being for the request as it stands.",
      "", pointer.stale || "", rec);
  }
  if (live) {
    const seed = live.seed || {};
    if (live.status === "failed") {
      return built("failed", live, live.message
        ? `The instance did not come up: ${live.message}`
        : "The instance did not come up.", "instance", "", rec);
    }
    if (live.ready) return built("ready", live, "The instance is up and seeded for this build.", "", "", rec);
    if (live.status !== "ready") {
      if (stalled) return stalledState(live, rec);
      return built("provisioning", live, live.message
        ? `The instance is being built: ${live.message}`
        : "The instance is being built. It takes ten minutes or so.", "", "", rec);
    }
    if (seed.state === "failed" || seed.error) {
      return built("failed", live,
        `The instance is up and seeding failed${seed.error ? `: ${seed.error}` : "."}`, "seed", "", rec);
    }
    return built("seeding", live, "The instance is up and its synthetic data is being written.", "", "", rec);
  }
  if (pointer && pointer.pointed) {
    return built("unknown", null,
      "The kernel has a sandbox pointed at this build. The control plane did not "
      + "say whether anything is running, so this does not claim it.", "", "", rec);
  }
  // The control plane did not answer, and the kernel has the record all the same.
  if (stalled) return stalledState(null, rec);
  return null;
}

/** `provisioning` while a stalled sandbox is being replaced, `failed` once the
 *  replacement stalled too. The detail is the record's own sentence. */
function stalledState(live, rec) {
  const says = rec.says || recoveryWords(rec)?.title || "The sandbox stalled.";
  if (rec.state === "failed") return built("failed", live, says, "stalled", "", rec);
  return built("provisioning", live, says, "", "", rec);
}

function built(state, live, detail, failedStep = "", staleWhy = "", recovery = null) {
  return {
    state,
    detail,
    failedStep: state === "failed" ? failedStep : "",
    staleWhy: state === "stale" ? staleWhy : "",
    // Only a sandbox that is up and seeded has a page worth opening.
    appUrl: state === "ready" ? String(live?.appUrl || "") : "",
    name: String(live?.name || ""),
    // What the provisioner recorded about its being late, or null (LO-192).
    recovery: recovery || null,
  };
}

/** A state in a word, for the chip. Never the colour alone. */
export const STATE_WORD = {
  provisioning: "provisioning",
  seeding: "seeding",
  ready: "ready",
  stale: "stale",
  failed: "failed",
  unknown: "state not readable",
  none: "no sandbox yet",
};

// -- steps and progress ----------------------------------------------------------

export const STEP_WORD = {
  done: "done",
  active: "in progress",
  waiting: "waiting",
  failed: "failed",
};

/** The newest QA run, or null. */
export const runOf = (qa) => qa?.run || null;

/**
 * Three steps, in the order a sandbox goes through them: the instance is built,
 * its synthetic data is written, and the QA run explores it as each persona.
 * Each is one of `done`, `active`, `waiting`, `failed`, from the states above
 * and the run's own status, so a step is never "done" because time has passed.
 */
export function steps(box, qa) {
  const state = box?.state || "none";
  const run = runOf(qa);
  let instance = "waiting";
  let seed = "waiting";
  if (state === "provisioning") instance = "active";
  if (state === "seeding") { instance = "done"; seed = "active"; }
  if (state === "ready") { instance = "done"; seed = "done"; }
  if (state === "failed") {
    if (box.failedStep === "seed") { instance = "done"; seed = "failed"; } else instance = "failed";
  }
  let review = "waiting";
  if (state === "ready" && run) {
    review = run.status === "running" ? "active"
      : run.status === "passed" ? "done" : "failed";
  }
  return [
    { key: "instance", label: "Instance", state: instance },
    { key: "seed", label: "Seed data", state: seed },
    { key: "qa", label: "QA run", state: review },
  ];
}

/** 0 to 100 from the steps: a done step counts whole and an active one half. */
export function progressOf(list) {
  const score = list.reduce((sum, s) => sum + (s.state === "done" ? 1 : s.state === "active" ? 0.5 : 0), 0);
  return Math.round((score / list.length) * 100);
}

// -- the QA report, in the order a reader needs it --------------------------------

const ORDER = { fail: 0, skipped: 1, pass: 2 };

/** Failing checks first, then the ones that could not be made, then the passes,
 *  each group in the order the report listed them. */
export function orderChecks(checks) {
  return (checks || [])
    .map((check, index) => ({ check, index }))
    .sort((a, b) => (ORDER[a.check.status] ?? 3) - (ORDER[b.check.status] ?? 3) || a.index - b.index)
    .map((x) => x.check);
}

export function countChecks(checks) {
  const out = { fail: 0, skipped: 0, pass: 0, total: 0 };
  for (const c of checks || []) {
    out.total += 1;
    if (c.status in out) out[c.status] += 1;
  }
  return out;
}

/**
 * The report as the groups the screen draws: the checks that are about the run
 * as a whole, then one group per persona. Groups with something wrong come
 * first, so the first thing a reader meets is the failing one.
 */
export function groupsOf(run) {
  if (!run) return [];
  const groups = [];
  if ((run.checks || []).length) {
    groups.push({ key: "run", name: "The whole run", persona: null, status: null,
                  checks: orderChecks(run.checks), counts: countChecks(run.checks) });
  }
  for (const p of run.personas || []) {
    groups.push({ key: `persona:${p.name}`, name: p.name, persona: p, status: p.status,
                  checks: orderChecks(p.checks), counts: countChecks(p.checks) });
  }
  const bad = (g) => g.counts.fail > 0 || g.status === "failed" || g.status === "error";
  return groups
    .map((g, index) => ({ g, index }))
    .sort((a, b) => Number(bad(b.g)) - Number(bad(a.g)) || a.index - b.index)
    .map((x) => x.g);
}

/**
 * What the report says of whether a persona may open the app (LO-211), for the reviewer's detail: the
 * kernel's own answer to the persona (`access`, read from its `my-access`), what it lacks, and what the
 * run did about it. Empty for a persona that has access, which is the usual one, and for a report from
 * before the kernel said. A persona whose answer was not known is said to have been explored as one who
 * may open the app, which is what the run did.
 */
export function accessLine(persona) {
  const a = persona?.access;
  if (!a || typeof a !== "object") return "";
  if (a.has_access === false) {
    const lacks = (a.missing || []).map(wordsOf).join(", ") || "access it could not name";
    return `This persona lacks ${lacks}, by its own my-access answer. The run expected the missing-permissions screen naming exactly that, looked at that one screen, and pressed nothing on it.`;
  }
  if (a.has_access === null || a.has_access === undefined) {
    return `Whether this persona may open the app was not known (${a.detail || "the kernel was not asked"}), so it was explored as one who may.`;
  }
  return "";
}

/** What a persona's status is called. A pass says what it was a pass of. */
export function personaWord(status) {
  return {
    passed: "passed on synthetic data",
    failed: "failed",
    error: "did not complete",
    skipped: "skipped",
  }[status] || String(status || "unknown");
}

/** What a check's status is called. `skipped` says it is not a pass, and a failure
 *  the kernel marked `inconclusive` (LO-193: it could not be judged, and nothing the
 *  app did failed) says whose it is. */
export function checkWord(status, check) {
  if (status === "fail" && check?.inconclusive === true) return "inconclusive, not a finding about the app";
  return { pass: "passed", fail: "failed", skipped: "skipped, not a pass" }[status] || String(status || "unknown");
}

/** The checks of a run, run-wide and each persona's, that failed because they could
 *  not be judged. A check the kernel did not mark is not in it. */
export function inconclusiveChecks(run) {
  const every = [...(run?.checks || []), ...(run?.personas || []).flatMap((p) => p.checks || [])];
  return every.filter((c) => c.status === "fail" && c.inconclusive === true);
}

/**
 * The run in one line, for somebody who reads only that.
 *
 * `tone` picks the colour and `word` is the word that goes with it. A pass is
 * only ever called what the kernel calls it, and only when it is for the
 * request as it stands: a run that passed for permissions that have since
 * changed is said as that, because "passed" with nothing after it is the
 * overstatement this product exists to prevent.
 */
export function headline(qa) {
  const run = runOf(qa);
  if (!run) {
    return { tone: "none", word: "No run yet",
             text: "No QA run has been made for this sandbox." };
  }
  const s = run.summary || {};
  if (run.status === "running") {
    return { tone: "running", word: "Running",
             text: "A QA run is exploring the sandbox as each persona." };
  }
  if (run.status === "error") {
    const why = run.error?.detail || run.error?.reason || "";
    return { tone: "error", word: "Did not complete",
             text: `The run did not finish${why ? `: ${why}` : "."} That is the platform's fault and not a finding about the app, and it is not a pass.` };
  }
  if (run.status === "failed") {
    const n = s.failed ?? 0;
    const unjudged = inconclusiveChecks(run);
    // Every failure is one that could not be judged: nothing the app did failed,
    // and the run is still not a pass.
    if (n > 0 && unjudged.length === n) {
      const ids = [...new Set(unjudged.map((c) => c.id))].join(", ");
      return { tone: "inconclusive", word: "Inconclusive",
               text: `${n} ${n === 1 ? "check" : "checks"} could not be judged (${ids}), which is not a finding about the app and is not a pass.` };
    }
    return { tone: "failed", word: "Failed",
             text: n
               ? `${n} ${n === 1 ? "check" : "checks"} failed${s.personas ? ` across ${s.personas} ${s.personas === 1 ? "persona" : "personas"}` : ""}${unjudged.length ? `, ${unjudged.length} of them inconclusive (not a finding about the app)` : ""}.`
               : "The run failed." };
  }
  if (run.status === "passed") {
    if (run.bound_to_current === false || qa?.gate?.passed_for_current === false) {
      return { tone: "stale", word: "Passed for earlier permissions",
               text: "A run passed, but for permissions that have since changed, so it does not count for this request." };
    }
    return { tone: "passed", word: "Passed", text: qa?.label || run.label || PASS_LABEL };
  }
  return { tone: "none", word: String(run.status || "unknown"), text: "" };
}

/** `abc` as `Abc.`, for a sentence the kernel sent without either. */
function sentence(text) {
  const t = String(text || "").trim();
  if (!t) return "";
  return `${t[0].toUpperCase()}${t.slice(1)}${/[.!?]$/.test(t) ? "" : "."}`;
}

/** A failure from the kernel in its own words: the sentence, then its code.
 *  The kernel says `detail`; the gateway in front of it says `message` when it
 *  answers for itself (a route it does not have yet), and that is its sentence.
 *  Either is shown as it came and neither is paraphrased. */
export function refusalWords(failure) {
  if (!failure) return { status: 0, code: "", text: "" };
  return {
    status: failure.status || 0,
    code: failure.error || failure.body?.error || "",
    text: failure.body?.detail || failure.body?.message || failure.message || "",
  };
}

// -- the Submit button -----------------------------------------------------------

/**
 * Whether Submit for approval is pressable, and when it is not, the run that is
 * missing or failed, by name. A refusal is information: "disabled" with no
 * reason is the screen refusing and keeping the answer to itself.
 *
 * `qa` is `GET /sandboxes/{id}/qa`, whose `gate.passed_for_current` is the
 * kernel's own answer to "did a run pass for this build and this manifest" and
 * the one field the hard gate (LO-183) reads. This reads it and does not
 * recompute it. `can: true` is not permission: the kernel decides at submit,
 * and what it says is shown as it said it.
 *
 *  - `kind` is for the screen's own tests and for which sentence to hang a hint
 *    on; `reason` is the whole sentence.
 *  - `revision` (LO-206, `revisionOf`) holds it while the chat has moved past this
 *    draft and the builder is at work on what comes next. That is the Builder's
 *    own rule and not the kernel's: the gate above binds a pass to the draft's
 *    own build and manifest and does not know a revision was asked for, so the
 *    hold carries a `basis` that says so, shown beside the reason. It is read
 *    after the request's own state (a request already sent is said as sent) and
 *    before the run's, which is about a draft the person is no longer asking for.
 */
export function submitGate({ request, qa, qaFailure, box, revision }) {
  if (!request) {
    return { can: false, kind: "loading", reason: "Reading the request." };
  }
  if (request.state === "pending") {
    return { can: false, kind: "sent",
             reason: "Already sent for approval. A governor decides it on the console's Requests screen." };
  }
  if (request.state !== "draft") {
    return { can: false, kind: "closed",
             reason: `This request is ${request.state === "superseded" ? "replaced" : request.state}. Only a draft can be submitted.` };
  }
  if (revision?.blocksSubmit && revision.hold) {
    return { can: false, kind: "revising", reason: revision.hold.reason, basis: revision.hold.basis };
  }
  if (qaFailure) {
    const said = refusalWords(qaFailure);
    return { can: false, kind: "unreadable",
             reason: `The QA result could not be read${said.text ? `: ${said.text}` : "."}` };
  }
  if (!qa) {
    return { can: false, kind: "loading", reason: "Reading the QA result." };
  }
  if (qa.gate?.passed_for_current) {
    return { can: true, kind: "passed", reason: "" };
  }

  const run = runOf(qa);
  const parts = [sentence(qa.gate?.reason) || "No run has passed for this build and these permissions."];
  let kind = "no_run";
  if (run) {
    if (run.bound_to_current === false) {
      kind = "stale_run";
      parts.push("The newest run was made for earlier permissions, so it does not count.");
    } else if (run.status === "running") {
      kind = "running";
    } else if (run.status === "failed") {
      kind = "failed";
      const failed = run.summary?.failed_ids || [];
      const unjudged = new Set(inconclusiveChecks(run).map((c) => c.id));
      if (failed.length && failed.every((id) => unjudged.has(id))) {
        // Nothing the app did failed (LO-193): the gateway that keeps the egress
        // log was not confirmed working, so the run could not be judged.
        kind = "inconclusive";
        parts.push(`Inconclusive: ${failed.join(", ")}, which is not a finding about the app. The gateway that keeps the egress log was not confirmed working, so QA cannot say what the function called; run QA again once the gateway is working.`);
      } else if (failed.length) {
        parts.push(`Failed: ${failed.map((id) => (unjudged.has(id) ? `${id} (inconclusive, not a finding about the app)` : id)).join(", ")}.`);
      }
    } else if (run.status === "error") {
      kind = "error";
      const why = run.error?.detail || run.error?.reason || "";
      parts.push(`The run did not complete${why ? `: ${why}` : ""}. That is not a finding about the app; run it again.`);
    }
  }
  if (box && box.state && box.state !== "ready") {
    parts.push(`The sandbox is ${STATE_WORD[box.state] || box.state}${box.state === "failed" ? "" : ", and QA starts once it is ready"}.`);
  }
  return { can: false, kind, reason: parts.join(" ") };
}

// -- the sandbox link ------------------------------------------------------------

const TOKEN = /^[A-Za-z0-9._~-]+$/;

/**
 * The sandbox's address with the viewer's own bearer on the fragment, which is
 * how the kit's sandbox bar learns who is looking (`remote-auth`, LO-188): it
 * reads `#viewer_token=`, keeps it for the tab and takes it out of the address
 * bar.
 *
 * A fragment is never sent to a server, which is why the token rides there and
 * not in a query. The kit reads it raw and does not decode it, so it is put in
 * raw; a token that is not made of the characters a signed JWT is made of is
 * refused rather than guessed at, since a bearer that arrives altered fails
 * somewhere that names neither the link nor the token. Anything but a web
 * address is refused as well. `null` means there is no link to open.
 */
export function viewerUrl(appUrl, token) {
  if (!appUrl || !token || !TOKEN.test(token)) return null;
  let url;
  try {
    url = new URL(appUrl);
  } catch {
    return null;
  }
  const local = ["localhost", "127.0.0.1"].includes(url.hostname);
  if (url.protocol !== "https:" && !(local && url.protocol === "http:")) return null;
  const kept = url.hash.replace(/^#/, "").split("&")
    .filter((part) => part && !part.startsWith("viewer_token="));
  url.hash = [...kept, `viewer_token=${token}`].join("&");
  return url.toString();
}

// -- screenshots -----------------------------------------------------------------

/**
 * Where a screenshot is asked for. The report names one as
 * `screenshots/<persona>/<file>.png` (`check.screenshot`,
 * `persona.screenshots[].path`) and that is the tail of the route the kernel
 * serves it at (LO-182): `/sandboxes/{id}/qa/` + the name, with `?run_id=` of
 * the report it came from, which answers a short-lived link. One place builds
 * the path so that if the route's shape moves, so does this and nothing else.
 */
export function screenshotPath(requestId, name) {
  const clean = String(name || "").split("/").filter((s) => s && s !== "..").map(encodeURIComponent).join("/");
  return `/sandboxes/${encodeURIComponent(requestId)}/qa/${clean}`;
}

/** The query that goes with it: the run whose report names the screenshot. A
 *  name with no run would be looked for in the newest report that lists it,
 *  which is a different run from the one on screen as soon as there is a newer. */
export const screenshotQuery = (runId) => (runId ? { run_id: runId } : {});

/**
 * What a refused screenshot means, in a sentence, before the kernel's own.
 * The code decides the sentence and the kernel's words follow it, so a code
 * this does not know is still said, in the kernel's words alone.
 */
export function screenshotWhy(failure) {
  const code = failure?.error || failure?.body?.error || "";
  const meaning = {
    screenshot_missing: "This screenshot is no longer stored.",
    unknown_screenshot: "The kernel does not list this screenshot for that run.",
    bad_screenshot_name: "The name the report gives is not one the kernel will serve.",
    unknown_run: "That run is no longer there.",
  }[code] || "";
  const said = failure?.message || failure?.body?.detail || "";
  if (failure?.status === 0) return `${meaning || "The kernel could not be reached."} ${said}`.trim();
  return [meaning, said].filter(Boolean).join(" ");
}

/** The link in the kernel's answer, or `null` when it is not one to put in an `<img>`. */
export function screenshotSource(answer) {
  const url = answer?.url;
  if (typeof url !== "string") return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && !(["localhost", "127.0.0.1"].includes(parsed.hostname) && parsed.protocol === "http:")) return null;
  } catch {
    return null;
  }
  return { url, expiresIn: Number(answer.expires_in) || 0, contentType: String(answer.content_type || "") };
}

/** Every screenshot a persona's part of the report names, once each. */
export function screenshotsOf(group) {
  const seen = new Map();
  const add = (path, label) => {
    if (typeof path === "string" && path && !seen.has(path)) seen.set(path, label || "");
  };
  for (const s of group.persona?.screenshots || []) add(s.path, s.label);
  return [...seen].map(([path, label]) => ({ path, label }));
}

// -- the logs --------------------------------------------------------------------

/** The call log in one sentence: how many, and how they were answered. */
export function callsLine(calls) {
  const s = calls?.summary;
  if (!s) return "";
  if (!s.total) return "No kernel call has been logged for this sandbox yet.";
  return `${s.total} kernel ${s.total === 1 ? "call" : "calls"}: ${s.allowed} allowed, ${s.empty} empty, ${s.refused} refused.`;
}

/** What an unused grant is called. The kernel's own `detail` is the sentence. */
export const GRANT_KIND = {
  read: "read",
  service_read: "read, nobody signed in",
  write: "write",
  service_write: "write, nobody signed in",
  aggregate: "aggregate",
};

/** The gateway's own words for its state, as a chip says them. Never the colour alone. */
export const GATEWAY_WORD = {
  healthy: "working",
  silent: "silent",
  recording_failed: "cannot record",
  unconfirmed: "not confirmed",
  never_reported: "never reported",
  unreadable: "state not readable",
};

/**
 * Whether the gateway that fills the egress log was working (LO-193), for a chip
 * and a sentence, or `null` when there is no log to speak of. The state, the
 * sentence and the last failed write are the kernel's (`gateway` in
 * `GET /sandboxes/{id}/egress`) and are carried as sent; this adds a word and a
 * tone and nothing about what counts as working. A kernel that sends no block is
 * `unknown` and not a working gateway.
 */
export function gatewayView(egress) {
  if (!egress) return null;
  const g = egress.gateway;
  if (!g || typeof g !== "object") {
    return { state: "unknown", word: "not reported", tone: "warning", healthy: false,
             detail: "The kernel did not say whether the gateway is working.", lastError: "", lastErrorAt: "" };
  }
  const state = String(g.state || "unknown");
  const healthy = g.healthy === true;
  return {
    state,
    word: GATEWAY_WORD[state] || state.replace(/_/g, " "),
    tone: healthy ? "success" : "warning",
    healthy,
    detail: String(g.detail || ""),
    lastError: String(g.last_error || ""),
    lastErrorAt: String(g.last_error_at || ""),
  };
}

const READINGS = ["hosts_recorded", "nothing_contacted", "not_recorded"];

/** How the kernel says to read the host list: `hosts_recorded`, `nothing_contacted`
 *  (empty, and the gateway working) or `not_recorded` (empty, and it was not), or
 *  `unknown` for an answer that does not say. Not worked out here. */
export function egressReading(egress) {
  const state = egress?.reading?.state;
  return READINGS.includes(state) ? state : "unknown";
}

/**
 * The egress log in one sentence. An empty log is said as one of three different
 * things, because they are different: nothing was contacted (the gateway was
 * working and recorded no attempt), not recorded (it was not working, so the list
 * says nothing about the function), and unknown (the kernel did not say). None of
 * them is said as clean, and a list from a gateway that was not working says it
 * may be incomplete.
 */
export function egressLine(egress) {
  if (!egress) return "";
  const s = egress.summary || {};
  const hosts = (egress.hosts || []);
  const reading = egressReading(egress);
  if (!egress.recorded && !hosts.length) {
    if (reading === "nothing_contacted") {
      return `${egress.reading.detail} That is a reading and not proof the function made no call: code that ignores the proxy settings is stopped by the network and leaves no row.`;
    }
    if (reading === "not_recorded") return egress.reading.detail;
    return "No outbound attempt has been logged. That is not proof the function made none. The kernel did not say whether the gateway is working, so this is not read as nothing contacted.";
  }
  const distinct = new Set(hosts.map((h) => h.host)).size;
  const blocked = s.blocked_hosts ?? 0;
  const gateway = egress.gateway;
  const incomplete = gateway && gateway.healthy === false
    ? ` The gateway is not confirmed working (${String(gateway.state || "unknown").replace(/_/g, " ")}), so this list may be incomplete.`
    : "";
  return `${distinct} ${distinct === 1 ? "host" : "hosts"} tried through the gateway${blocked ? `, ${blocked} blocked` : ", none blocked"}.${incomplete}`;
}

/** A timestamp a person can read, or what was sent when it is not one. */
export function when(iso) {
  if (!iso) return "";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? String(iso) : at.toLocaleString();
}

// -- looking again ---------------------------------------------------------------

const OPEN = ["draft", "pending"];

/**
 * Which kind of waiting the panel is in, for `sandboxPollDelay`: or `done` when
 * nothing will change by itself and looking again would only spend calls.
 *
 *  - a request that is not a draft or pending has no sandbox (it goes with the
 *    request);
 *  - a sandbox that failed, or that belongs to a closed request, stays as it is
 *    until somebody acts;
 *  - a ready sandbox is waiting on its run until one exists, polled while it is
 *    running, and finished once the newest run has an outcome.
 */
export function phaseOf({ request, requestFailure, box, qa }) {
  if (requestFailure && [403, 404].includes(requestFailure.status)) return "done";
  if (request && !OPEN.includes(request.state)) return "done";
  const state = box?.state || "none";
  if (state === "failed") return "done";
  if (state === "provisioning") return "provisioning";
  if (state === "seeding") return "seeding";
  if (state === "stale") return box.staleWhy === "closed" ? "done" : "stale";
  if (state === "unknown") return "unknown";
  if (state === "ready") {
    const run = runOf(qa);
    if (!run) return "waiting";
    return run.status === "running" ? "qa" : "done";
  }
  return "waiting";
}

/** A short fingerprint of what the panel shows, so a look that found the same
 *  thing can be told from one that found something new. */
export function panelFingerprint({ request, box, control, qa }) {
  const run = runOf(qa);
  return JSON.stringify([
    request?.state || "", request?.proposed?.build_id || "", request?.request_id || "",
    box?.state || "", control?.current?.status || "", control?.current?.seed?.state || "",
    recoveryOf(request, control)?.state || "", recoveryOf(request, control)?.attempt || 0,
    run?.run_id || "", run?.status || "", (run?.personas || []).map((p) => p.status).join(","),
    qa?.gate?.passed_for_current ? 1 : 0,
  ]);
}
